import { apiClient } from '@/api/client'
import { type ApiResponse } from '@/api/types'

import type {
  AccessTokenResponse,
  BrandLoginResponse,
  BrandUser,
  LoginCredentials,
  MeResponse,
  SetPasswordPayload,
} from './auth.types'
import { capabilitiesForPlan, normalizeCapabilities } from './capabilities'
import { tokenService } from './token.service'

// All brand auth endpoints are mounted under /api/v1/brand (PR #1855, DEV-1594).
const BASE = '/api/v1/brand'

// Endpoints that authenticate with the refresh token (access_tokens, rotate)
// override the bearer with the refresh token; the auth endpoints skip the
// automatic access-token refresh-on-401 in the client to avoid recursion.
export const brandAuthApi = {
  // POST /auth/login → 201 { data: { access_token, refresh_token } }.
  login: async (credentials: LoginCredentials) =>
    (
      await apiClient.post<ApiResponse<BrandLoginResponse>>(`${BASE}/auth/login`, {
        body: credentials,
        skipAuthRefresh: true,
      })
    ).data,

  // DELETE /auth/logout → 204. Revokes ALL of the user's refresh tokens.
  logout: () => apiClient.delete<undefined>(`${BASE}/auth/logout`, { skipAuthRefresh: true }),

  // POST /auth/forgot-password → always 200 (non-enumerating).
  forgotPassword: (email: string) =>
    apiClient.post<undefined>(`${BASE}/auth/forgot-password`, {
      body: { email },
      skipAuthRefresh: true,
    }),

  // POST /auth/set-password → 200 (invite + reset flows). No auto-login.
  setPassword: (payload: SetPasswordPayload) =>
    apiClient.post<undefined>(`${BASE}/auth/set-password`, {
      body: payload,
      skipAuthRefresh: true,
    }),

  // GET /me → { data: { capabilities, user, company, terms_accepted } }.
  fetchMe: async () => (await apiClient.get<ApiResponse<MeResponse>>(`${BASE}/me`)).data,

  // PATCH /me/accept-terms → 200. Records ToS acceptance.
  acceptTerms: () => apiClient.patch<undefined>(`${BASE}/me/accept-terms`),

  // POST /access_tokens → exchanges the refresh token for a fresh access token.
  async refreshAccessToken(): Promise<AccessTokenResponse> {
    const refreshToken = tokenService.getRefreshToken()
    if (!refreshToken) {
      throw new Error('No refresh token available')
    }
    const res = await apiClient.post<ApiResponse<AccessTokenResponse>>(`${BASE}/access_tokens`, {
      skipAuthRefresh: true,
      headers: { 'X-Authorization': `Bearer ${refreshToken}` },
    })
    tokenService.setTokens({ accessToken: res.data.access_token, refreshToken })
    return res.data
  },

  // NOTE: POST /refresh_tokens/rotate (refresh-token rotation) is available as a
  // future hardening step; the functional flow only needs access-token refresh.
}

// Maps the GET /me wire payload onto the FE `BrandUser` model. The /me endpoint
// returns first/last name separately and only { id, name } for the company.
export function meToBrandUser(me: MeResponse): BrandUser {
  const name = [me.user.first_name, me.user.last_name].filter(Boolean).join(' ').trim()

  const plan = me.company.brand_dashboard_plan

  // Merge the (interim) plan-derived capabilities with the authoritative BE map.
  // The plan bridge now fills only the Placements capabilities the BE hasn't
  // shipped yet; the KPI + subscription capabilities come straight from /me and
  // overlay on top. Drop the bridge once /me returns Placements too. See
  // capabilities.ts.
  const capabilities = {
    ...capabilitiesForPlan(plan),
    ...normalizeCapabilities(me.capabilities),
  }

  return {
    type: 'brand_user',
    id: String(me.user.id),
    email: me.user.email,
    first_name: me.user.first_name,
    last_name: me.user.last_name,
    name: name || undefined,
    role: me.user.role,
    locale: me.user.locale,
    company: {
      type: 'company',
      id: String(me.company.id),
      name: me.company.name,
      brand_dashboard_plan: plan,
      country: me.company.country,
      data_available_from: me.company.data_available_from,
      cancelled_to: me.company.cancelled_to,
    },
    capabilities,
    terms_required: !me.terms_accepted,
  }
}
